// ===========================================
// Two-Factor Authentication API Functions
// Phase 2 - Priority 6
// ===========================================

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export interface TwoFactorSetupResponse {
  status: 'success';
  data: {
    secret: string;
    qrCodeUrl: string;
    issuer: string;
    accountName: string;
  };
}

export interface TwoFactorVerifySetupRequest {
  code: string;
}

export interface BackupCodesResponse {
  status: 'success';
  data: {
    codes: string[];
    createdAt: string;
    codesRemaining: number;
  };
}

export interface TwoFactorStatusResponse {
  status: 'success';
  data: {
    enabled: boolean;
    enabledAt: string | null;
    backupCodesRemaining: number;
  };
}

export interface TwoFactorDisableRequest {
  password: string;
  code: string;
}

export interface TwoFactorDisableResponse {
  status: 'success';
  message: string;
}

export interface RegenerateBackupRequest {
  code: string;
}

export interface TwoFactorVerifyLoginRequest {
  tempToken: string;
  code: string;
  trustDevice?: boolean;
}

export interface TwoFactorVerifyLoginResponse {
  status: 'success';
  data: {
    accessToken: string;
    refreshToken: string;
    user: {
      id: string;
      email: string;
      firstName: string;
      lastName: string;
      role: string;
      schoolId: string;
    };
    backupCodeUsed?: boolean;
    remainingBackupCodes?: number;
  };
}

export interface TrustedDevice {
  id: string;
  deviceName: string | null;
  userAgent: string | null;
  ipAddress: string | null;
  trustedUntil: string;
  lastUsedAt: string | null;
  createdAt: string;
  isCurrent: boolean;
}

export interface TrustedDevicesResponse {
  status: 'success';
  data: {
    devices: TrustedDevice[];
  };
}

export interface RevokeDeviceResponse {
  status: 'success';
  message: string;
}

export interface RevokeAllDevicesResponse {
  status: 'success';
  data: {
    removedCount: number;
  };
}

// Admin types
export interface UserTwoFactorStatus {
  userId: string;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  twoFactorEnabled: boolean;
  twoFactorVerifiedAt: string | null;
  backupCodesUsed: number;
  trustedDevicesCount: number;
  lastLoginAt: string | null;
}

export interface AdminUserStatusResponse {
  status: 'success';
  data: UserTwoFactorStatus;
}

export interface AdminForceResetRequest {
  reason?: string;
}

export interface AdminForceResetResponse {
  status: 'success';
  message: string;
}

export interface ComplianceReportResponse {
  status: 'success';
  data: {
    users: UserTwoFactorStatus[];
  };
}

export interface SecuritySettings {
  twoFactorRequired: {
    ADMIN: boolean;
    TEACHER: boolean;
    PARENT: boolean;
    STUDENT: boolean;
  };
  twoFactorGracePeriodDays: number;
  deviceTrustDays: number;
}

export interface SecuritySettingsResponse {
  status: 'success';
  data: SecuritySettings;
}

export interface UpdateSecuritySettingsRequest {
  twoFactorRequired?: Partial<SecuritySettings['twoFactorRequired']>;
  twoFactorGracePeriodDays?: number;
  deviceTrustDays?: number;
}

// ===========================================
// USER 2FA API FUNCTIONS
// ===========================================

/**
 * Initiate 2FA setup - returns secret and QR code
 */
export async function initiate2FASetup(): Promise<TwoFactorSetupResponse> {
  return apiClient.post<TwoFactorSetupResponse>('/auth/2fa/setup', {});
}

/**
 * Verify 2FA setup with TOTP code
 */
export async function verify2FASetup(
  data: TwoFactorVerifySetupRequest
): Promise<BackupCodesResponse> {
  return apiClient.post<BackupCodesResponse>('/auth/2fa/verify-setup', data);
}

/**
 * Get current 2FA status
 */
export async function get2FAStatus(): Promise<TwoFactorStatusResponse> {
  return apiClient.get<TwoFactorStatusResponse>('/auth/2fa/status');
}

/**
 * Disable 2FA (requires password and current code)
 */
export async function disable2FA(
  data: TwoFactorDisableRequest
): Promise<TwoFactorDisableResponse> {
  return apiClient.post<TwoFactorDisableResponse>('/auth/2fa/disable', data);
}

/**
 * Regenerate backup codes (requires current 2FA code)
 */
export async function regenerateBackupCodes(
  data: RegenerateBackupRequest
): Promise<BackupCodesResponse> {
  return apiClient.post<BackupCodesResponse>('/auth/2fa/regenerate-backup', data);
}

/**
 * Verify 2FA code during login (completes 2FA login flow)
 */
export async function verify2FALogin(
  data: TwoFactorVerifyLoginRequest
): Promise<TwoFactorVerifyLoginResponse> {
  return apiClient.post<TwoFactorVerifyLoginResponse>('/auth/2fa/verify', data);
}

// ===========================================
// TRUSTED DEVICES API FUNCTIONS
// ===========================================

/**
 * Get list of trusted devices
 */
export async function getTrustedDevices(): Promise<TrustedDevicesResponse> {
  return apiClient.get<TrustedDevicesResponse>('/auth/2fa/devices');
}

/**
 * Revoke a specific trusted device
 */
export async function revokeDevice(deviceId: string): Promise<RevokeDeviceResponse> {
  return apiClient.delete<RevokeDeviceResponse>(`/auth/2fa/devices/${deviceId}`);
}

/**
 * Revoke all trusted devices
 */
export async function revokeAllDevices(): Promise<RevokeAllDevicesResponse> {
  return apiClient.delete<RevokeAllDevicesResponse>('/auth/2fa/devices');
}

// ===========================================
// ADMIN 2FA API FUNCTIONS
// ===========================================

/**
 * Get user 2FA status (admin only)
 */
export async function getAdminUserStatus(
  userId: string
): Promise<AdminUserStatusResponse> {
  return apiClient.get<AdminUserStatusResponse>(`/auth/2fa/admin/users/${userId}/status`);
}

/**
 * Force reset user 2FA (admin only)
 */
export async function adminForceReset2FA(
  userId: string,
  data: AdminForceResetRequest
): Promise<AdminForceResetResponse> {
  return apiClient.post<AdminForceResetResponse>(
    `/auth/2fa/admin/users/${userId}/reset`,
    data
  );
}

/**
 * Get compliance report (users without 2FA)
 */
export async function getComplianceReport(): Promise<ComplianceReportResponse> {
  return apiClient.get<ComplianceReportResponse>('/auth/2fa/admin/compliance');
}

/**
 * Get school security settings
 */
export async function getSecuritySettings(): Promise<SecuritySettingsResponse> {
  return apiClient.get<SecuritySettingsResponse>('/auth/2fa/admin/settings');
}

/**
 * Update school security settings
 */
export async function updateSecuritySettings(
  data: UpdateSecuritySettingsRequest
): Promise<SecuritySettingsResponse> {
  return apiClient.put<SecuritySettingsResponse>('/auth/2fa/admin/settings', data);
}
